import { useState } from 'react';
import { Link } from 'react-router-dom';
import { requestRegistration } from '../api/endpoints';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import logoSrc from '../img/SB-logo01.jpg';

export function RegistrationPage() {
  useDocumentTitle('Zugang anfragen');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [organization, setOrganization] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestRegistration({ email, displayName, organization, message });
      setSuccess(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err.message ?? 'Anfrage fehlgeschlagen';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200/60 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Anfrage gesendet!</h1>
          <p className="text-gray-600 mb-6">
            Ihre Registrierungsanfrage wurde gesendet. Ein Administrator wird diese prüfen
            und Sie per E-Mail benachrichtigen.
          </p>
          <Link
            to="/login"
            className="text-indigo-600 font-medium hover:text-indigo-800"
          >
            ← Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
      <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200/60 w-full max-w-md">
        <div className="text-center mb-6">
          <img src={logoSrc} alt="Symbolbuch.de Logo" className="w-16 h-16 mx-auto mb-3 rounded-2xl object-contain" />
          <h1 className="text-2xl font-bold text-gray-900">
            Zugang anfragen
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Füllen Sie das Formular aus. Ein Administrator wird Ihre Anfrage prüfen.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="reg-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              id="reg-name"
              type="text"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Ihr Vor- und Nachname"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder:text-gray-400"
            />
          </div>

          <div>
            <label htmlFor="reg-email" className="block text-sm font-medium text-gray-700 mb-1">
              E-Mail *
            </label>
            <input
              id="reg-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ihre.email@einrichtung.de"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder:text-gray-400"
            />
          </div>

          <div>
            <label htmlFor="reg-org" className="block text-sm font-medium text-gray-700 mb-1">
              Team / Einrichtung
            </label>
            <input
              id="reg-org"
              type="text"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder="z. B. Lebenshilfe Musterstadt"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder:text-gray-400"
            />
          </div>

          <div>
            <label htmlFor="reg-message" className="block text-sm font-medium text-gray-700 mb-1">
              Nachricht an den Administrator
            </label>
            <textarea
              id="reg-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Warum möchten Sie Symbolbuch.de nutzen?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none placeholder:text-gray-400"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
          >
            {loading ? 'Wird gesendet…' : 'Zugang anfragen'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <Link to="/login" className="text-sm text-gray-500 hover:text-indigo-600">
            Bereits Zugang? → Anmelden
          </Link>
        </div>
      </div>
    </div>
  );
}
